import { PickingArea, PickingAreaCreatePayload, PickingColor, PickingType, SortedSide } from '@ekkogmbh/apisdk';
import { ListItemText, MenuItem } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ContentPanel, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import useEkko from 'src/Common/Providers/EkkoProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import { CenteredColumn, ReactiveRow } from 'src/Common/Components/Stacks';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';
import { PickingColors } from '../Helper/PickingColors';
import { SortedSideSelect } from './SortedSideSelect';
import { StyledCheckbox } from 'src/Common/Components/Forms/StyledCheckbox';
interface Props {
  pickingArea?: PickingArea;
  closeHandler: () => void;
}
export const PickingAreaPanel = ({ pickingArea, closeHandler }: Props): React.JSX.Element => {
  const { api, config } = useEkko();
  const { refresh } = usePagination();
  const { enableSave } = usePanel();

  const [, startSaving] = useRequest();

  const [externalIdentifier, setExternalIdentifier] = useState<string>('');
  const [pickingType, setPickingType] = useState<PickingType>(PickingType.PICK_BY);
  const [isRequiredSideAck, setRequiredSideAck] = useState<boolean>(false);
  const [isOptionalSideAck, setOptionalSideAck] = useState<boolean>(false);
  const [isSymmetricalAck, setSymmetricalAck] = useState<boolean>(false);
  const [colors, setColors] = useState<PickingColor[]>([PickingColor.GREEN]);
  const [requiredSide, setRequiredSide] = useState<SortedSide | undefined>();
  const [optionalSide, setOptionalSide] = useState<SortedSide | undefined>();
  const [defaultBlinkDuration, setDefaultBlinkDuration] = useState<number>(120);
  const [blinkCount, setBlinkCount] = useState<number>(10);
  const [jobCount, setJobCount] = useState<number>(1);
  const [ackTopic, setAckTopic] = useState<string>('');
  const [isAutoFinish, setAutoFinish] = useState<boolean>(false);

  const savePayload = useMemo((): PickingAreaCreatePayload | undefined => {
    if ([externalIdentifier].includes('') || requiredSide === undefined) {
      return undefined;
    }

    return {
      id: externalIdentifier,
      type: pickingType,
      requiredSide,
      requiredSideAck: isRequiredSideAck,
      optionalSide: optionalSide ?? null,
      optionalSideAck: optionalSide ? isOptionalSideAck : null,
      symmetricalAck: optionalSide ? isSymmetricalAck : undefined,
      colors,
      defaultBlinkDuration,
      simultaneousBlinkCount: blinkCount,
      maximumSimultaneouslyRunningJobs: jobCount,
      ackByButtonTopic: ackTopic === '' ? undefined : ackTopic,
      automaticJobFinish: isAutoFinish,
    };
  }, [
    externalIdentifier,
    pickingType,
    requiredSide,
    isRequiredSideAck,
    optionalSide,
    isOptionalSideAck,
    isSymmetricalAck,
    colors,
    defaultBlinkDuration,
    blinkCount,
    jobCount,
    ackTopic,
    isAutoFinish,
  ]);

  const save = useCallback(() => {
    if (savePayload === undefined) {
      return;
    }

    startSaving(
      async () => {
        if (pickingArea) {
          await api.updatePickingArea(savePayload);
        } else {
          await api.addPickingArea(savePayload);
        }
      },
      () => {
        refresh();
        closeHandler();
      },
    );
  }, [pickingArea, savePayload, closeHandler]);

  const reset = useCallback(() => {
    setExternalIdentifier(pickingArea?.id ?? '');
    setPickingType(pickingArea?.type ?? PickingType.PICK_BY);
    setRequiredSideAck(pickingArea?.requiredSideAck ?? true);
    setOptionalSideAck(pickingArea?.optionalSideAck ?? false);
    setSymmetricalAck(pickingArea?.symmetricalAck ?? false);
    setColors(pickingArea?.colors ?? [PickingColor.GREEN]);
    setRequiredSide(pickingArea?.requiredSide as SortedSide);
    setOptionalSide((pickingArea?.optionalSide ?? undefined) as SortedSide | undefined);
    setDefaultBlinkDuration(pickingArea?.defaultBlinkDuration ?? 120);
    setBlinkCount(pickingArea?.simultaneousBlinkCount ?? 10);
    setJobCount(pickingArea?.maximumSimultaneouslyRunningJobs ?? 1);
    setAckTopic(pickingArea?.ackByButtonTopic ?? '');
    setAutoFinish(pickingArea?.automaticJobFinish ?? false);
  }, [pickingArea]);

  useEffect(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    enableSave(savePayload !== undefined);
  }, [savePayload]);

  /** force optional ack off if there is no side */
  useEffect(() => {
    if (optionalSide === undefined && isOptionalSideAck) {
      setOptionalSideAck(false);
    }
  }, [optionalSide, isOptionalSideAck]);

  const ackTopicOptions = useMemo(() => {
    const configOptions = config.ackByButtonTopics.map((topic, i) => (
      <MenuItem key={i} value={topic}>
        <ListItemText primary={topic} />
      </MenuItem>
    ));
    configOptions.unshift(
      <MenuItem key={'disable'} value={''}>
        <ListItemText primary={'disabled'} />
      </MenuItem>,
    );
    return configOptions;
  }, [config.ackByButtonTopics]);

  return (
    <ContentPanel
      steps={[
        {
          title: 'Picking Area',
          contentCallback: () => (
            <ReactiveRow>
              <CenteredColumn width={'100%'} spacing={2}>
                <StyledTextField
                  type={'text'}
                  label={'External-Identifier'}
                  value={externalIdentifier}
                  onChange={(e) => {
                    setExternalIdentifier(e.target.value as string);
                  }}
                  disabled={pickingArea !== undefined}
                />
                <StyledSelectField
                  label={'Type'}
                  value={pickingType}
                  onChange={(e) => {
                    setPickingType(e.target.value as PickingType);
                  }}
                  disabled={pickingArea !== undefined}
                >
                  <MenuItem value={'pick-by'}>PickBy</MenuItem>
                  <MenuItem value={'pick-to'}>PickTo</MenuItem>
                  <MenuItem value={'pick-combination'}>PickCombination</MenuItem>
                </StyledSelectField>
                <StyledSelectField
                  multiple
                  label={'Colors'}
                  value={[...colors]}
                  onChange={(e) => {
                    setColors(e.target.value as PickingColor[]);
                  }}
                >
                  {PickingColors.map((color: PickingColor, index: number) => (
                    <MenuItem key={index} value={color}>
                      {color}
                    </MenuItem>
                  ))}
                </StyledSelectField>
                <StyledTextField
                  type={'number'}
                  label={'Blink-Count'}
                  value={blinkCount}
                  onChange={(e) => {
                    let blinkNumber = parseInt(e.target.value);
                    if (isNaN(blinkNumber)) {
                      blinkNumber = 0;
                    }
                    setBlinkCount(blinkNumber);
                  }}
                  tooltip={'Maximum number of blinking labels'}
                />
                <StyledTextField
                  type={'number'}
                  label={'Blink-Duration'}
                  value={defaultBlinkDuration}
                  onChange={(e) => {
                    let duration = parseInt(e.target.value);
                    if (isNaN(duration)) {
                      duration = 0;
                    }
                    setDefaultBlinkDuration(duration);
                  }}
                />
                <StyledTextField
                  type={'number'}
                  label={'Concurrent-Jobs'}
                  value={jobCount}
                  onChange={(e) => {
                    let count = parseInt(e.target.value);
                    if (isNaN(count)) {
                      count = 1;
                    }
                    setJobCount(count);
                  }}
                  tooltip={'Maximum number of running picking orders'}
                />
              </CenteredColumn>
              <CenteredColumn width={'100%'} spacing={2}>
                <SortedSideSelect
                  label={'Required Picking-Side'}
                  value={requiredSide}
                  onChange={setRequiredSide}
                  disabled={pickingArea !== undefined}
                />
                <SortedSideSelect
                  label={'Optional Picking-Side'}
                  value={optionalSide}
                  onChange={setOptionalSide}
                  disabled={pickingArea !== undefined}
                />
                <StyledSelectField
                  label={'Button-Acknowledgement-Topic'}
                  value={ackTopic}
                  onChange={(e) => {
                    console.log(e.target.value);
                    setAckTopic(e.target.value as string);
                  }}
                  disabled={ackTopicOptions.length === 0}
                >
                  {ackTopicOptions}
                </StyledSelectField>
                <StyledCheckbox
                  label={'Required-Side Ack'}
                  value={isRequiredSideAck}
                  onChange={() => {
                    setRequiredSideAck(!isRequiredSideAck);
                  }}
                  disabled={pickingArea !== undefined}
                  tooltip={'Acknowledge required side once per pick'}
                />
                <StyledCheckbox
                  label={'Optional-Side Ack'}
                  value={isOptionalSideAck}
                  onChange={() => {
                    setOptionalSideAck(!isOptionalSideAck);
                  }}
                  disabled={optionalSide === undefined || pickingArea !== undefined}
                  tooltip={'Acknowledge optional side once per order'}
                />
                <StyledCheckbox
                  label={'Symmetrical Ack'}
                  value={isSymmetricalAck}
                  onChange={() => {
                    setSymmetricalAck(!isSymmetricalAck);
                  }}
                  disabled={pickingArea !== undefined}
                  tooltip={'Acknowledge both sides for every pick'}
                />
                <StyledCheckbox
                  label={'Automatically Finish Jobs'}
                  value={isAutoFinish}
                  onChange={() => {
                    setAutoFinish(!isAutoFinish);
                  }}
                  tooltip={'Automatically set picking order jobs to finished when all values are acknowledged'}
                />
              </CenteredColumn>
            </ReactiveRow>
          ),
        },
      ]}
      onSave={save}
      onReset={reset}
      onCancel={closeHandler}
    />
  );
};
